<template>
    <AdminPageInner v-if="course.id" :breadcrumbs="breadcrumbs">
        <template #heading>Edit: {{ course.title }}</template>
        <CourseForm
            v-if="formComponent === 'CourseForm'"
            :course="course"
            :mainImage="course.main_image"
            :thumbnailImage="course.thumbnail_image"
            @submit="onSubmit">
            <template #footer>
                <div class="flex flex-row-reverse items-center">
                    <SubmitButton
                        :submitting="form.processing"
                        :disabled="form.errors.any()">
                        {{ requiresSessionDates ? 'Next' : 'Update' }}
                    </SubmitButton>
                    <router-link :to="redirectRoute" class="text-gray-500 mr-4">
                        Cancel
                    </router-link>
                </div>
            </template>
        </CourseForm>
        <SessionDatesForm
            v-if="formComponent === 'SessionDatesForm'"
            :course="course"
            :sessionGroups="sessionGroups"
            @submit="onSubmit">
            <template #footer>
                <div class="flex flex-row-reverse items-center">
                    <SubmitButton
                        :submitting="form.processing"
                        :disabled="form.errors.any()">
                        Update
                    </SubmitButton>
                    <button type="button" @click.prevent="formComponent === 'CourseForm'" class="text-gray-500 mr-4">
                        Back
                    </button>
                </div>
            </template>
        </SessionDatesForm>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import AdminPageInner from "@/Components/Layout/AdminPageInner";
import CourseForm from "@/Pages/Admin/Courses/Components/Form";
import SubmitButton from "@/Components/Form/SubmitButton";
import SessionDatesForm from "@/Pages/Admin/Courses/Components/SessionDatesForm";

export default {

    name: "CourseEditPage",

    data() {
        return {
            course: {},
            sessionGroups: [],
            retrievedSessionGroups: false,
            form: this.$form(),
            formComponent: 'CourseForm',
        }
    },

    provide() {
        return {
            form: this.form
        }
    },

    components: {
        AdminPageInner,
        CourseForm,
        SessionDatesForm,
        SubmitButton
    },

    methods: {
        setData(course) {
            this.form.withData({
                title: course.title,
                summary: course.summary,
                description: course.description,
                pathway_id: course.pathway_id,
                credits: course.credits,
                multipart: course.multipart,
                main_image_id: course.main_image_id,
                thumbnail_image_id: course.thumbnail_image_id,
                type: course.type,
                trainer_ids: course.trainer_ids,
                moderator_ids: course.moderator_ids,
                publish: !!course.published_at,
                modules: course.modules,
                course_sessions: [],
                validate_only: false
            });
            this.course = course;
        },
        onSubmit() {

            if (this.form.processing) {
                return;
            }

            this.form.validate_only = this.requiresSessionDates && this.formComponent === 'CourseForm';

            this.form.put(route('api.courses.update', this.course))
                .then(async course => {
                    if (!this.form.validate_only) {
                        this.$notify.success('Course updated');
                        await this.$router.push(this.redirectRoute);
                    }
                })
                .catch(() => {
                    if (this.form.validate_only) {
                        this.form.errors.clear('course_sessions');
                    }
                })
                .then(() => {
                    if (this.form.validate_only && !this.form.errors.any()) {
                        this.buildCourseSessions();
                        this.formComponent = 'SessionDatesForm';
                    }
                });
        },
        async fetchUpcomingSessionGroups() {

            const data = (await axios.get(route('api.course-session-groups.index'), {
               params: {
                   course_id: this.course.id,
                   upcoming: true,
                   per_page: 100
               }
            })).data;

            this.sessionGroups = data.data;
        },
        buildCourseSessions() {

            let sessions = [];

            for (const sessionGroup of this.sessionGroups) {
                for (const module of this.newModules) {
                    const existing = this.form.course_sessions.find(item => {
                        return item.module_id === module._id && item.course_session_group_id === sessionGroup.id;
                    });

                    sessions.push(existing ? existing : {
                        module_id: module._id,
                        course_session_group_id: sessionGroup.id,
                        type: null,
                        location_url: null,
                        location_notes: null,
                        start: null,
                        end: null,
                    })
                }
            }

            this.form.course_sessions = sessions;
        }
    },

    computed: {
        breadcrumbs() {
            return [
                {label: 'Admin', to: {name: 'admin'}},
                {label: 'Courses', to: {name: 'admin.courses.index'}},
                {
                    label: `${this.course.title || 'Course'}`,
                    to: {name: 'admin.courses.show', params: this.$route.params}
                },
                {label: `Edit`, to: {name: this.$route.name, params: this.$route.params}},
            ];
        },
        redirectRoute() {
            return this.$route.meta.previous ? this.$route.meta.previous : {name: 'admin.courses.index'};
        },
        newModules() {
            return this.form.modules
                ? this.form.modules.filter(item => item.hasOwnProperty('_id'))
                : [];
        },
        hasNewModule() {
            return this.newModules.length > 0;
        },
        requiresSessionDates() {
            return this.hasNewModule && this.sessionGroups.length;
        }
    },

    watch: {
        hasNewModule(hasNewModule) {
            if (hasNewModule && !this.retrievedSessionGroups) {
                this.fetchUpcomingSessionGroups();
                this.retrievedSessionGroups = true;
            }
        },
        formComponent() {
            document.querySelector('#app > div').scrollTo(0, 0);
        }
    },

    async beforeRouteEnter(to, from, next) {
        const data = (await axios.get(route('api.courses.edit', to.params.courseId), {params: to.query})).data;
        next(vm => vm.setData(data));
    },
};
</script>
